// AGC: Financial service — TanStack Query hooks for expenses, revenues, safras
// AGC: Replaces the internal logic of FinancialContext
// AGC: Uses centralized api.ts for token injection; Query handles caching/refetch
// AGC: localStorage disk cache for instant display on mount (stale-while-revalidate)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import type { Expense, Revenue, Safra } from '../types'

// AGC: Input types for creating records
export interface CreateExpenseData {
  title: string
  value: number
  date: string
  category?: string
  notes?: string
}

export interface CreateRevenueData {
  title: string
  value: number
  date: string
  category?: string
  notes?: string
}

export interface CreateSafraData {
  name: string
  year: string
  grain: string
  dateStart: string
  dateEnd: string
}

// ============================================================
// AGC: LOCALSTORAGE DISK CACHE
// AGC: Write on fetch success, read as placeholderData on mount
// ============================================================

const CACHE_KEYS = {
  expenses: 'droidfarm_expenses',
  revenues: 'droidfarm_revenues',
  safras: 'droidfarm_safras',
} as const

function readCache<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return undefined
    return JSON.parse(raw) as T
  } catch {
    return undefined
  }
}

function writeCache<T>(key: string, data: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(data))
  } catch { /* AGC: ignore quota errors */ }
}

// ============================================================
// AGC: Query hooks — fetch server data
// ============================================================

// AGC: Backend wraps arrays in { success, expenses/revenues/safras, count } — unwrap here
export function useExpensesQuery(enabled = true) {
  return useQuery({
    queryKey: ['expenses'],
    queryFn: async () => {
      const res = await api.get<{ expenses: Expense[] }>('/api/v1/expenses')
      const data = res.expenses ?? []
      writeCache(CACHE_KEYS.expenses, data)
      return data
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: () => readCache<Expense[]>(CACHE_KEYS.expenses),
  })
}

export function useRevenuesQuery(enabled = true) {
  return useQuery({
    queryKey: ['revenues'],
    queryFn: async () => {
      const res = await api.get<{ revenues: Revenue[] }>('/api/v1/revenues')
      const data = res.revenues ?? []
      writeCache(CACHE_KEYS.revenues, data)
      return data
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: () => readCache<Revenue[]>(CACHE_KEYS.revenues),
  })
}

export function useSafrasQuery(enabled = true) {
  return useQuery({
    queryKey: ['safras'],
    queryFn: async () => {
      const res = await api.get<{ safras: Safra[] }>('/api/v1/safras')
      const data = res.safras ?? []
      writeCache(CACHE_KEYS.safras, data)
      return data
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: () => readCache<Safra[]>(CACHE_KEYS.safras),
  })
}

// ============================================================
// AGC: Mutation hooks — create/delete
// AGC: Each mutation updates both TanStack Query cache + localStorage
// ============================================================

export function useCreateExpenseMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateExpenseData) => api.post<Expense>('/api/v1/expenses', data),
    onSuccess: (newExpense) => {
      qc.setQueryData<Expense[]>(['expenses'], (old) => {
        const updated = [newExpense, ...(old || [])]
        writeCache(CACHE_KEYS.expenses, updated)
        return updated
      })
    },
  })
}

export function useDeleteExpenseMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/expenses/${id}`),
    onSuccess: (_, id) => {
      qc.setQueryData<Expense[]>(['expenses'], (old) => {
        const updated = (old || []).filter((e) => e.id !== id)
        writeCache(CACHE_KEYS.expenses, updated)
        return updated
      })
    },
  })
}

export function useCreateRevenueMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateRevenueData) => api.post<Revenue>('/api/v1/revenues', data),
    onSuccess: (newRevenue) => {
      qc.setQueryData<Revenue[]>(['revenues'], (old) => {
        const updated = [newRevenue, ...(old || [])]
        writeCache(CACHE_KEYS.revenues, updated)
        return updated
      })
    },
  })
}

export function useDeleteRevenueMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/revenues/${id}`),
    onSuccess: (_, id) => {
      qc.setQueryData<Revenue[]>(['revenues'], (old) => {
        const updated = (old || []).filter((r) => r.id !== id)
        writeCache(CACHE_KEYS.revenues, updated)
        return updated
      })
    },
  })
}

export function useCreateSafraMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateSafraData) => api.post<Safra>('/api/v1/safras', data),
    onSuccess: (newSafra) => {
      qc.setQueryData<Safra[]>(['safras'], (old) => {
        const updated = [newSafra, ...(old || [])]
        writeCache(CACHE_KEYS.safras, updated)
        return updated
      })
    },
  })
}

export function useDeleteSafraMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/safras/${id}`),
    onSuccess: (_, id) => {
      qc.setQueryData<Safra[]>(['safras'], (old) => {
        const updated = (old || []).filter((s) => s.id !== id)
        writeCache(CACHE_KEYS.safras, updated)
        return updated
      })
    },
  })
}
